import CommonService from "../Common";
import { AxiosRequestConfig } from "axios";

export interface PlatformData {
  name: string;
  currentPlan: string;
  currentCost: number;
  _id?: string;
  lastSync?: string | null;
  isActive?: boolean;
  key?: string;
  type?: string;
  userId?: string;
  image?: string;
  apiEndpoint?: string;
  costApiAvailable?: boolean;
  dataExportFormat?: "json" | "csv" | "xml";
}

interface StatusData {
  isActive: boolean;
  userId: string;
}

interface PricingData {
  currentPlan: string;
  currentCost: number;
  userId: string;
}

interface UsageQueryParams {
  startDate?: string;
  endDate?: string;
}

class PlatformService {
  pathPrefix: string;
  commonService: CommonService;

  constructor() {
    this.pathPrefix = "/api/v1/platform";
    this.commonService = new CommonService();
  }

  // Get all platforms for the current user
  getAllPlatforms(userId: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}?userId=${userId}`,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Get a specific platform by ID
  getPlatform(id: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${id}`,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Create multiple platforms in batch (even if just one)
  batchCreatePlatforms(platforms: Array<PlatformData>, userId: string) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/batch`,
      data: { platforms, userId },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Update platform status (active/inactive)
  updateStatus(id: string, statusData: StatusData) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${id}/status`,
      data: statusData,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Update platform status (active/inactive)
  updatePricing(id: string, pricingData: PricingData) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${id}/pricing`,
      data: pricingData,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Update platform cost_center tag (chargeback)
  updateCostCenter(id: string, cost_center: string) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${id}/cost-center`,
      data: { cost_center },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Update platform next billing date
  updateNextBillingDate(
    id: string,
    data: { nextBillingDate: number; userId: string }
  ) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${id}/billing-date`,
      data,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Get usage data for a platform
  getUsage(id: string, params?: UsageQueryParams) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${id}/usage`,
      params,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Sync usage data for a platform with external API
  syncUsage(id: string, formData: FormData) {
    let config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${id}/sync`,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }
}

export default PlatformService;
